/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {
    AppNavigator,
    copyToClipboard,
    directTimeout,
    getMode,
    getSetting,
    Loader,
    remToPx,
    ResponseData,
    showDialog,
    useEvent
} from '@dvrd/dvr-controls';
import {googleLogout, GoogleOAuthProvider} from '@react-oauth/google';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router';
import HeaderController from './components/header/headerController';
import APIConnectionBar from './components/dashboard/apiConnectionBar';
import {AppContext} from './components/misc/appContext';
import {LayoutContext} from './components/misc/layoutContext';
import {startIdleObserver, stopIdleObserver} from './components/misc/idleObserver';
import PopupController from './components/misc/popupController';
import URLActions from './components/misc/urlActions';
import SidebarMenuController from './components/sidebarMenu/sidebarMenuController';
import BolmateContent from './components/misc/bolmateContent';
import Account from './models/accountModel';
import User from './models/userModel';
import {getDefaultRoute} from './util/accessUtil';
import {hasAdminLogin, onAdminLogout} from './util/adminUtil';
import {sendGaEvent, setGaUserId} from './util/analytics';
import {onConnectBolOauth} from './util/bolOauthUtil';
import {ADMIN_USERS_ROUTE, LOGIN_ROUTE, SELECTED_ACCOUNT_KEY} from './util/constants';
import {onAddAccount, onGlobalLoad} from './util/events';
import {clearBeaconIds, setBeaconIds} from './util/helpscoutUtil';
import {requireMaintenanceLogin} from './util/maintenanceUtil';
import {setMenuWidth} from './util/menuUtil';
import {logout} from './util/requestSender';
import {setSentryUser} from './util/sentryUtil';
import {onShowConfirmSnackbar} from './util/renderUtil';
import {deleteFromLocal, getFromLocal, saveToAppLocal, saveToLocal} from './util/storageUtil';
import {UserType} from './util/interfaces';
import InfoBanner from './components/misc/infoBanner';
import {getCurrentLanguage, getLanguageFromUser} from './i18n/languageService';
import {UI_LANGUAGE_FLEX_KEY} from './i18n/config';

function demoLogin(): boolean {
    const params = new URLSearchParams(location.search);
    if (params.get('demo') === '1') {
        setGaUserId(null);
        return true;
    }
    return false;
}

function getSelectedAccount(user: User): Account | null {
    const {accounts} = user;
    if (accounts.length) {
        const selectedId = getFromLocal(SELECTED_ACCOUNT_KEY) ?? accounts[0].id;
        for (const account of accounts) if (account.id === selectedId) return account;
        return accounts[0];
    }
    return null;
}

function renderingCalculator(): boolean {
    return window.location.pathname.includes('prijs-calculator');
}

const devMode = getMode() === 'dev';

export default function App() {
    const {t, i18n} = useTranslation();
    const context = useContext(AppContext);
    const {sidebarRef} = useContext(LayoutContext);
    const {user, validatingUser} = context.userContext;
    const [menuWidth, _setMenuWidth] = useState(remToPx(12.5));
    const stripe = useMemo(() => {
        const stripeApiKey: string = getSetting('stripeApiKey');
        return loadStripe(stripeApiKey);
    }, []);
    const navigate = useNavigate();
    useEvent('onLogout', _onLogout);

    function onClickAdminId(id: string) {
        return function () {
            copyToClipboard(id);
            onShowConfirmSnackbar(t('app.admin.idCopied', {id, defaultValue: '{{id}} gekopieerd'}));
        };
    }

    function getEnvTitle(): string | undefined {
        const mode = getMode();
        if (mode === 'dev') return t('app.env.dev', 'Develop omgeving');
        if (mode === 'accept') return t('app.env.accept', 'Acceptatie omgeving');
        if (['test', 'sub'].includes(mode)) return t('app.env.test', 'Test omgeving');
        return undefined;
    }

    function renderAdminStatsBanner() {
        if (!user || (!hasAdminLogin() && user.type !== UserType.SUPER_ADMIN)) return null;
        const {account} = context.accountContext;
        const apiKey = account?.getAPIKey() ?? null;
        return (
            <InfoBanner variant='critical' title={getEnvTitle()} persistDismiss>
                <span className='admin-stats-banner'>
                    <label className='key'>{t('app.admin.user', 'Gebruiker')}:</label>
                    <br />
                    <label onClick={onClickAdminId(user.id)} className='click' title={t('common.copy', 'Kopieren')}>
                        {user.id}
                    </label>
                    {account !== null && (
                        <>
                            <label className='key'>{t('app.admin.account', 'Account')}:</label>
                            <br />
                            <label
                                onClick={onClickAdminId(account.id)}
                                className='click'
                                title={t('common.copy', 'Kopieren')}
                            >
                                {account.id}
                            </label>
                        </>
                    )}
                    {apiKey !== null && (
                        <>
                            <label className='key'>{t('app.admin.apiKey', 'API key')}:</label>
                            <br />
                            <label
                                onClick={onClickAdminId(apiKey.id)}
                                className='click'
                                title={t('common.copy', 'Kopieren')}
                            >
                                {apiKey.id}
                            </label>
                        </>
                    )}
                </span>
            </InfoBanner>
        );
    }

    function onClickApiFlow(evt: React.MouseEvent) {
        sendGaEvent('Click', 'Dashboard', 'API keys instellen');
        const account = context.accountContext.account;
        if (!account) return;
        saveToLocal(SELECTED_ACCOUNT_KEY, account.id);
        onConnectBolOauth(evt.shiftKey);
    }

    function _onLogout() {
        if (!context.userContext.user) return;
        onGlobalLoad(true);
        logout({
            callback: (data: ResponseData) => {
                onGlobalLoad(false);
                if (data.success) {
                    clearBeaconIds();
                    googleLogout();
                    if (data.logged_in) {
                        // Still logged in as admin
                        onAdminLogout()
                            .then((user: User) => {
                                if (user.accounts.length) saveToLocal(SELECTED_ACCOUNT_KEY, user.accounts[0].id);
                                context.onChangeContext(
                                    {
                                        user,
                                        accounts: user.accounts,
                                        account: user.accounts.length ? user.accounts[0] : null
                                    },
                                    () => {
                                        navigate(ADMIN_USERS_ROUTE);
                                    }
                                );
                            })
                            .catch(() => {
                                deleteFromLocal(SELECTED_ACCOUNT_KEY);
                                setGaUserId(null);
                                context.onChangeContext({accounts: [], account: null, user: null});
                                navigate(LOGIN_ROUTE);
                            });
                    } else {
                        deleteFromLocal(SELECTED_ACCOUNT_KEY);
                        setGaUserId(null);
                        context.onChangeContext({accounts: [], account: null, user: null});
                        navigate(LOGIN_ROUTE);
                    }
                } else showDialog('Het uitloggen is niet gelukt.', 'Uitloggen mislukt');
            }
        });
    }

    function validateUser() {
        if (requireMaintenanceLogin()) {
            context.onChangeContext({validatingUser: false});
            return;
        }
        if (demoLogin()) return startApplication(null);
        User.get(
            null,
            (user: User | null) => {
                if (!user || (user.isDemoAccount && !devMode)) {
                    return startApplication(null);
                }
                setGaUserId(user.id);
                const account = getSelectedAccount(user);
                setBeaconIds(user, account);
                if (user.isNewUser) saveToAppLocal('newUser', true);
                context.onChangeContext(
                    {
                        user,
                        accounts: user.accounts,
                        account: account
                    },
                    () => {
                        if (account) setSentryUser(user, account);
                        else {
                            setSentryUser(user);
                            directTimeout(onAddAccount);
                        }
                        startApplication(user, account);
                    }
                );
            },
            () => {
                startApplication(null);
            }
        );
    }

    function startApplication(user: User | null, account?: Account | null) {
        if (user && (!user.isDemoAccount || devMode)) {
            if (/inloggen|registreren/.test(window.location.pathname)) {
                const searchParams = new URLSearchParams(location.search);
                const redirect = searchParams.get('redirect');
                if (redirect) navigate(redirect);
                else {
                    const defaultRoute = getDefaultRoute(account?.userRights, account?.userRole);
                    navigate(defaultRoute);
                }
            }
        }
        context.onChangeContext({validatingUser: false});
    }

    function onSetMenuWidth() {
        const element = sidebarRef.current;
        if (!element) return;
        const width = element.getBoundingClientRect().width;
        _setMenuWidth(width);
    }

    useEffect(() => {
        setMenuWidth(menuWidth);
    }, [menuWidth]);

    useEffect(() => {
        if (user && !menuWidth) window.setTimeout(onSetMenuWidth, 500);
    }, [user]);

    const userLanguageFlex: string | null = user?.getFlex(UI_LANGUAGE_FLEX_KEY) ?? null;
    useEffect(() => {
        let userLanguage = getLanguageFromUser(user);
        if (!userLanguage) userLanguage = getCurrentLanguage();
        void i18n.changeLanguage(userLanguage);
    }, [user?.id, userLanguageFlex]);

    useEffect(() => {
        validateUser();
        startIdleObserver();
        return function () {
            stopIdleObserver();
        };
    }, []);

    if (validatingUser)
        return (
            <>
                <Loader active />
                <AppNavigator />
            </>
        );
    return (
        <GoogleOAuthProvider clientId='85081984902-vov3r4rckqdh843l7pf8r8bdsbqqhe7u'>
            <AppNavigator />
            {!!user && !renderingCalculator() ? (
                <>
                    <HeaderController menuWidth={menuWidth} />
                    <div className='page-content'>
                        <SidebarMenuController />
                        <Elements stripe={stripe}>
                            <BolmateContent>
                                <APIConnectionBar onClickApiFlow={onClickApiFlow} />
                                {renderAdminStatsBanner()}
                                <InfoBanner className='mobile-banner visible' variant='warning' persistDismiss={false}>
                                    {t(
                                        'app.mobileWarning',
                                        'Ons platform is niet geoptimaliseerd voor mobiele apparaten. Switch naar een laptop of desktop.'
                                    )}
                                </InfoBanner>
                                <Outlet />
                            </BolmateContent>
                        </Elements>
                    </div>
                </>
            ) : (
                <BolmateContent>
                    <Outlet />
                </BolmateContent>
            )}
            <URLActions />
            <PopupController />
        </GoogleOAuthProvider>
    );
}
